import React from 'react';
import { iFile } from '@/types';
import {
  PageLayout,
  SpecialZoomLevel,
  ViewMode,
  Viewer,
} from '@react-pdf-viewer/core';
// @mui
import {
  Box,
  SxProps,
  Tooltip,
  TooltipProps,
  styled,
  tooltipClasses,
} from '@mui/material';
//
import { files, fmt } from '@utils';
(async () => {
  const pdfjs = await import('pdfjs-dist/build/pdf');
  const pdfjsWorker = await import('pdfjs-dist/build/pdf.worker.entry');
  pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;
})();

// ----------------------------------------------------------------------

interface iFileThumbnail {
  sx?: SxProps;
  imgSx?: SxProps;
  onDownload?: () => void;
  file: iFile | string;
}

const StyledThumb = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: '#f4f6f8',
  display: 'flex',
  flexDirection: 'column',

  '& .Thumb-Caption': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(0.5, 1),

    '& .MuiSvgIcon-root': {
      color: theme.palette.text.primary,
      width: 15,
      height: 15,
    },
  },
}));

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
  },
}));

const FileThumbnail: React.FC<iFileThumbnail> = ({
  file,
  // onDownload,
  sx,
  imgSx,
}) => {
  const { name = '', path = '', preview = '' } = files.fileData(file);
  const format = files.fileFormat(path || preview);

  const pageLayout: PageLayout = {
    buildPageStyles: () => ({
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
    }),
  };

  const renderImage =
    format === 'image' ? (
      <Box
        component="img"
        src={preview}
        sx={{
          width: 1,
          height: 1,
          flexShrink: 0,
          objectFit: 'cover',
          ...imgSx,
        }}
      />
    ) : format === 'pdf' ? (
      <Box>
        <Viewer
          fileUrl={preview}
          pageLayout={pageLayout}
          viewMode={ViewMode.SinglePage}
          defaultScale={SpecialZoomLevel.PageWidth}
        />
      </Box>
    ) : (
      <Box
        component="img"
        src={files.fileThumb(format)}
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          ...sx,
        }}
      />
    );

  return (
    <StyledThumb onClick={(e) => e.stopPropagation()}>
      <Box overflow="hidden" p={0.5} borderRadius={1}>
        <BootstrapTooltip title={name} placement="bottom">
          {renderImage}
        </BootstrapTooltip>
      </Box>
    </StyledThumb>
  );
};

export default FileThumbnail;
